import React from "react";
import { Box, Container, Typography, Grid, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BRAND from "../theme/talktokam";

const stats = [
  { value: "5K+", label: "Bots Created" },
  { value: "500K+", label: "Conversations" },
  { value: "99.9%", label: "Uptime" },
  { value: "4.8\u2606", label: "Avg Rating" },
];

const values = [
  { icon: "\uD83D\uDCA1", title: "Innovation First", desc: "We build tools that push the boundaries of conversational AI, making enterprise-grade technology accessible to every business." },
  { icon: "\uD83E\uDD1D", title: "Customer-Centric", desc: "Every feature we ship starts with a real customer need. Our roadmap is shaped by conversations with thousands of businesses." },
  { icon: "\uD83D\uDD12", title: "Security & Trust", desc: "Domain whitelisting, lead data masking, 2FA, and strict privacy controls \u2014 your data stays yours." },
  { icon: "\uD83C\uDF0D", title: "Global by Design", desc: "Multi-language support, multi-platform embedding, and global-scale infrastructure from day one." },
];

const team = [
  { name: "Aman Shaikh", role: "Founder & CEO", emoji: "\uD83D\uDC68\u200D\uD83D\uDCBB" },
  { name: "Rahul Sharma", role: "CTO", emoji: "\uD83D\uDC68\u200D\uD83D\uDD2C" },
  { name: "Priya Patel", role: "Head of Product", emoji: "\uD83D\uDC69\u200D\uD83D\uDCBC" },
  { name: "Neha Gupta", role: "Lead Designer", emoji: "\uD83C\uDFA8" },
];

const About = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box sx={{ position: "relative", textAlign: "center", py: { xs: 10, md: 14 }, background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`, backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)", pointerEvents: "none" }} />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "5%", left: "-5%", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`, filter: "blur(60px)", pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", bottom: "-10%", right: "-10%", width: 600, height: 600, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "50%", right: "30%", width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`, filter: "blur(40px)", pointerEvents: "none" }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: "2rem", md: "3rem" }, background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 2 }}>
              💜 Made with Love for Better Conversations
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", color: "#94a3b8", mb: 4, maxWidth: 650, mx: "auto" }}>
              We believe every business deserves intelligent, automated conversations at scale \u2014 without needing an engineering team.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Box sx={{ maxWidth: 800, mx: "auto", textAlign: "center", mb: 6 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 2 }}>Our Story</Typography>
            <Typography sx={{ fontSize: "1rem", color: "#94a3b8", lineHeight: 1.8 }}>
              Talktokam was born from a simple observation: businesses spend too much time answering the same questions over and over.
              We wanted to create a platform that lets anyone \u2014 not just developers \u2014 build intelligent chatbots that feel like talking to a real person.
              <br /><br />
              Today, thousands of businesses across 40+ countries use Talktokam to automate conversations, capture leads, and provide instant support \u2014 24/7.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {stats.map((s, i) => (
            <Grid item xs={6} md={3} key={i}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Box sx={{ textAlign: "center", p: 3, borderRadius: 3, bgcolor: "rgba(255,255,255,0.05)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.08)", transition: "all 0.3s", "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(56,189,248,0.3)", boxShadow: "0 12px 40px rgba(56,189,248,0.1)" } }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "2rem", background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: "0.9rem", color: "#94a3b8" }}>{s.label}</Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mb: 6 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", textAlign: "center", mb: 4 }}>Our Values</Typography>
          <Grid container spacing={3}>
            {values.map((v, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <Box sx={{ p: 3, borderRadius: 4, bgcolor: "rgba(255,255,255,0.05)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.08)", height: "100%", transition: "all 0.3s", "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(56,189,248,0.3)", boxShadow: "0 12px 40px rgba(56,189,248,0.1)" } }}>
                    <Typography sx={{ fontSize: "2rem", mb: 1 }}>{v.icon}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#fff", mb: 1 }}>{v.title}</Typography>
                    <Typography sx={{ fontSize: "0.9rem", color: "#94a3b8", lineHeight: 1.7 }}>{v.desc}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 4 }}>Meet the Team</Typography>
          <Grid container spacing={3} justifyContent="center">
            {team.map((t, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <Box sx={{ textAlign: "center", p: 3, borderRadius: 4, bgcolor: "rgba(255,255,255,0.05)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.08)", transition: "all 0.3s", "&:hover": { transform: "translateY(-4px)", borderColor: "rgba(56,189,248,0.3)", boxShadow: "0 12px 40px rgba(56,189,248,0.1)" } }}>
                    <Typography sx={{ fontSize: "3rem", mb: 1 }}>{t.emoji}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff" }}>{t.name}</Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8" }}>{t.role}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Box sx={{ textAlign: "center", py: { xs: 6, md: 8 }, background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)" }}>
        <Container maxWidth="sm">
          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", background: "linear-gradient(135deg, #38bdf8, #a855f7)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", mb: 2 }}>Join thousands of businesses</Typography>
          <Typography sx={{ color: "#94a3b8", mb: 3 }}>Start your free trial today. No credit card required.</Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/signup")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 4, py: 1.5, background: "linear-gradient(135deg, #4F46E5, #7C3AED)", boxShadow: "0 4px 15px rgba(79,70,229,0.4)", "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" } }}>
            Get Started Free
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
